import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { ContentHeader, StatusBadge } from "@cx/ui";
import type { SalesOrdersReport } from "@cx/contracts";
import { BRAZIL_UFS, type Uf } from "@cx/domain";
import { useSession } from "../../session";
import { count, money, periodLabel, reportQuery, tileShare, UF_TILES } from "./labels";
import { statusList } from "./search";
import {
  FilterRow,
  ReportStatus,
  SettingsButton,
  StatusFilter,
  reportQueryOptions,
  useDashboardOptions,
} from "./shared";
import { ChannelTable, ProductTable } from "./widgets";
import { CompanyTabs } from "./CompanyTabs";

const PATH = "/meu-negocio/pedidos";

/**
 * Pedidos de venda (RF-BI-08): por UF (mapa em blocos clicável), por situação, por produto e por canal. Clicar numa
 * UF filtra produto, canal e situação por ela.
 */
export function SalesOrdersReportPage(): ReactElement {
  const { companyId, api } = useSession();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const options = useDashboardOptions();
  const statuses = statusList(search.statuses);
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const [from, to] = search.from && search.to ? [search.from, search.to] : [undefined, undefined];
  const d = useQuery({
    ...reportQueryOptions,
    queryKey: ["dashboard", companyId, "sales-orders", from, to, search.uf, search.statuses],
    queryFn: () =>
      api<SalesOrdersReport>(`/reports/sales-orders${reportQuery({ from, to, uf: search.uf, statuses })}`),
  });
  const r = d.data;
  const byUf = new Map(r?.byState.map((s) => [s.uf, s]) ?? []);
  const max = Math.max(0, ...(r?.byState.map((s) => s.totalCents ?? 0) ?? []));
  const noUf = byUf.get("");
  return (
    <>
      <ContentHeader
        title="Pedidos de venda"
        breadcrumb={["Meu Negócio", "Pedidos de venda"]}
        actions={<SettingsButton />}
      />
      <CompanyTabs />
      <FilterRow>
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="so-from">
            De
          </label>
          <input
            id="so-from"
            type="date"
            className="form-control"
            value={search.from ?? r?.from ?? ""}
            onChange={(e) => set({ from: e.target.value || undefined })}
          />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="so-to">
            Até
          </label>
          <input
            id="so-to"
            type="date"
            className="form-control"
            value={search.to ?? r?.to ?? ""}
            onChange={(e) => set({ to: e.target.value || undefined })}
          />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="so-uf">
            UF
          </label>
          <select
            id="so-uf"
            className="form-select"
            value={search.uf ?? ""}
            onChange={(e) => set({ uf: (e.target.value || undefined) as Uf | undefined })}
          >
            <option value="">Todas</option>
            {BRAZIL_UFS.map((u) => (
              <option key={u}>{u}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-3">
          {options.data && (
            <StatusFilter
              statuses={options.data.statuses}
              defaults={options.data.settings.saleStatusIds}
              value={statuses}
              onChange={(ids) => set({ statuses: ids?.join(",") })}
            />
          )}
        </div>
        <div className="col-12 col-md-auto">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => navigate({ search: {}, replace: true })}
          >
            Limpar filtros
          </button>
        </div>
      </FilterRow>
      <ReportStatus loading={d.isLoading} error={d.error} />
      {r && (
        <>
          <p className="text-muted small">
            {periodLabel(r)}
            {r.uf && ` · UF ${r.uf}`}
          </p>
          <div className="row g-3 mb-3">
            <div className="col-xl-5">
              <section className="box card h-100" aria-labelledby="uf-title">
                <div className="card-header">
                  <h2 id="uf-title" className="h6 mb-0">
                    Pedidos por UF
                  </h2>
                </div>
                <div className="card-body">
                  <div
                    className="d-grid gap-1"
                    style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))", maxWidth: "28rem" }}
                    data-testid="uf-grid"
                  >
                    {BRAZIL_UFS.map((u) => {
                      const s = byUf.get(u);
                      const share = tileShare(s?.totalCents ?? null, max);
                      const [row, col] = UF_TILES[u];
                      const active = r.uf === u;
                      return (
                        <button
                          key={u}
                          type="button"
                          className={`btn btn-sm px-0 fw-semibold ${active ? "btn-primary" : "btn-outline-secondary"}`}
                          style={{
                            gridRow: row + 1,
                            gridColumn: col + 1,
                            aspectRatio: "1",
                            ...(share && !active
                              ? {
                                  background: `rgba(var(--bs-primary-rgb), ${share})`,
                                  color: share > 0.5 ? "#fff" : undefined,
                                }
                              : {}),
                          }}
                          aria-pressed={active}
                          title={`${u}: ${money(s?.totalCents ?? 0)} em ${count(s?.orders ?? 0)} pedido(s)`}
                          aria-label={`${u}: ${money(s?.totalCents ?? 0)} em ${count(s?.orders ?? 0)} pedido(s)`}
                          onClick={() => set({ uf: active ? undefined : u })}
                        >
                          {u}
                        </button>
                      );
                    })}
                  </div>
                  {noUf && (
                    <p className="small text-muted mt-2 mb-0">
                      Sem UF no cadastro do cliente: {money(noUf.totalCents)} em {count(noUf.orders)}{" "}
                      pedido(s).
                    </p>
                  )}
                </div>
              </section>
            </div>
            <div className="col-xl-7">
              <section className="box card h-100" aria-label="Ranking de UFs" data-testid="by-state">
                <div className="card-header">
                  <h2 className="h6 mb-0">Ranking por UF</h2>
                </div>
                {r.byState.length === 0 ? (
                  <p className="text-muted p-3 mb-0">Sem vendas no período.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th scope="col">UF</th>
                          <th scope="col" className="text-end">
                            Valor
                          </th>
                          <th scope="col" className="text-end">
                            Pedidos
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.byState.map((s) => (
                          <tr key={s.uf || "-"}>
                            <td>{s.uf || "Sem UF"}</td>
                            <td className="text-end">{money(s.totalCents)}</td>
                            <td className="text-end">{count(s.orders)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          </div>
          <div className="row g-3 mb-3">
            <div className="col-xl-6">
              <section className="box card h-100" aria-label="Pedidos por situação" data-testid="by-status">
                <div className="card-header">
                  <h2 className="h6 mb-0">Pedidos por situação</h2>
                </div>
                {r.byStatus.length === 0 ? (
                  <p className="text-muted p-3 mb-0">Sem pedidos no período.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Situação</th>
                          <th scope="col" className="text-end">
                            Valor
                          </th>
                          <th scope="col" className="text-end">
                            Pedidos
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.byStatus.map((s) => (
                          <tr key={s.status.id}>
                            <td>
                              <StatusBadge tone={s.status.color}>{s.status.label}</StatusBadge>
                            </td>
                            <td className="text-end">{money(s.totalCents)}</td>
                            <td className="text-end">{count(s.orders)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
            <div className="col-xl-6">
              <ChannelTable rows={r.byChannel} />
            </div>
          </div>
          <div className="mb-3">
            <ProductTable title="Pedidos por produto" rows={r.byProduct} testId="by-product" />
          </div>
        </>
      )}
    </>
  );
}
